import { buildApiUrl } from '../../config/env';
import { storage } from '../../utils/storage';

export interface ReferralRequest {
  id: string;
  clubId: string;
  userId: string;
  businessName: string;
  businessDetails: string;
  lookingFor: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
    profession: string | null;
  };
  responses?: ReferralResponse[];
}

export interface ReferralResponse {
  id: string;
  requestId: string;
  responderId: string;
  contactName: string;
  contactPhone: string;
  contactDetails: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  responder: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
  };
  request?: ReferralRequest;
}

export interface LeaderboardEntry {
  rank: number;
  userId: string;
  points: number;
  user: {
    id: string;
    name: string | null;
    photo: string | null;
    businessName: string | null;
    profession: string | null;
  };
}

// Helper function to make authenticated API calls
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = await storage.getToken();
  const response = await fetch(buildApiUrl(endpoint), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Request failed');
  }

  return response.json();
};

// Get all referral requests for a club
export const getReferralRequests = async (clubId: string): Promise<ReferralRequest[]> => {
  return apiCall(`/api/referrals/club/${clubId}`);
};

// Get my referral requests (ones I posted)
export const getMyReferralRequests = async (clubId: string): Promise<ReferralRequest[]> => {
  return apiCall(`/api/referrals/my-requests/${clubId}`);
};

// Get my intros (responses I submitted)
export const getMyIntros = async (clubId: string): Promise<ReferralResponse[]> => {
  return apiCall(`/api/referrals/my-intros/${clubId}`);
};

// Create a new referral request
export const createReferralRequest = async (data: {
  clubId: string;
  businessName: string;
  businessDetails: string;
  lookingFor: string;
}): Promise<ReferralRequest> => {
  return apiCall('/api/referrals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Update an existing referral request
export const updateReferralRequest = async (
  requestId: string,
  data: {
    businessName: string;
    businessDetails: string;
    lookingFor: string;
  }
): Promise<ReferralRequest> => {
  return apiCall(`/api/referrals/${requestId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

// Submit an intro (response to a referral request)
export const submitIntro = async (
  requestId: string,
  data: {
    contactName: string;
    contactPhone: string;
    contactDetails: string;
    familiarity?: string;
  }
): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/respond/${requestId}`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

// Accept an intro
export const acceptIntro = async (responseId: string): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/accept/${responseId}`, { method: 'POST' });
};

// Reject an intro
export const rejectIntro = async (responseId: string): Promise<ReferralResponse> => {
  return apiCall(`/api/referrals/reject/${responseId}`, { method: 'POST' });
};

// Get leaderboard for a club (current month)
export const getLeaderboard = async (clubId: string): Promise<LeaderboardEntry[]> => {
  return apiCall(`/api/referrals/leaderboard/${clubId}`);
};

// Close a referral request
export const closeReferralRequest = async (requestId: string): Promise<ReferralRequest> => {
  return apiCall(`/api/referrals/close/${requestId}`, { method: 'POST' });
};
